import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PlayerRankingWidget from '../../components/stats/PlayerRankingWidget';
import {
  getCompetitionsForRankingBuilder,
  getCompetitionSeasonsForRankingBuilder,
  getRankingCompetitionMeta,
  getWidgetConfiguration,
} from '../../../api';
import { resolveWidgetDisplayTitle } from '../../utils/widgetDisplayTitle';

const DISCIPLINE_LABELS = {
  rugby: 'Rugby',
  soccer: 'Football',
};

const DISCIPLINE_OPTIONS = [
  { value: 'rugby', label: 'Rugby' },
  { value: 'soccer', label: 'Football' },
];

const getSeasonDisplayLabel = (seasonValue) => {
  const value = String(seasonValue || '').trim();
  if (!value) return '';

  if (value.includes('/')) {
    return value.split('/')[0];
  }

  return value;
};

const normalizeSeasonOptions = (rawSeasons = []) => {
  const byLabel = new Map();

  rawSeasons.forEach((seasonValue) => {
    const value = String(seasonValue || '').trim();
    if (!value) return;

    const label = getSeasonDisplayLabel(value);
    const existing = byLabel.get(label);

    if (!existing) {
      byLabel.set(label, {
        value,
        label,
      });
      return;
    }

    // On préfère garder la version la plus "complète"
    // ex: garder 2024/2025 plutôt que 2024
    if (!existing.value.includes('/') && value.includes('/')) {
      byLabel.set(label, {
        value,
        label,
      });
    }
  });

  return Array.from(byLabel.values()).sort((a, b) =>
    b.label.localeCompare(a.label)
  );
};

const MAX_SELECTED_COMPETITIONS = 5;

const clampCompetitionIds = (value = '') =>
  String(value || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .slice(0, MAX_SELECTED_COMPETITIONS);

const clampCompetitionObjects = (value = []) =>
  (Array.isArray(value) ? value : []).slice(0, MAX_SELECTED_COMPETITIONS);

export default function PlayerRankingBuilderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [widgetConfig, setWidgetConfig] = useState(null);
  const [widgetConfigLoading, setWidgetConfigLoading] = useState(false);
  const [widgetConfigError, setWidgetConfigError] = useState('');

  // const provider = searchParams.get('provider') || '';
  
  const discipline = searchParams.get('discipline') || '';
  const seasonParam = searchParams.get('season') || '';
  const seasonSinceParam = searchParams.get('seasonSince') || '';
  const competitionIdsParam = searchParams.get('competitionIds') || '';
  const widgetConfigurationId = searchParams.get('widgetConfigurationId') || '';

 const selectedCompetitionIds = useMemo(
  () => clampCompetitionIds(competitionIdsParam),
  [competitionIdsParam]
);

  const selectedSeasonsFromUrl = useMemo(
    () =>
      seasonParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [seasonParam]
  );

  const widgetSeason =
  selectedSeasonsFromUrl.length > 0
    ? selectedSeasonsFromUrl.join(',')
    : null;

  const [seasonOptions, setSeasonOptions] = useState([]);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);
  const [competitionsError, setCompetitionsError] = useState('');
  const seasonClearedManuallyRef = useRef(false);
  const competitionClearedManuallyRef = useRef(false);

  const [title, setTitle] = useState('Statistiques joueurs');
  const [metaLoading, setMetaLoading] = useState(false);

  const allowedDisciplines = useMemo(() => {
  if (Array.isArray(widgetConfig?.disciplines) && widgetConfig.disciplines.length) {
    return widgetConfig.disciplines;
  }
  if (widgetConfig?.discipline) {
    return [widgetConfig.discipline];
  }
  return [];
}, [widgetConfig]);

const titleCompetitionInfos = useMemo(
  () =>
    selectedCompetitionIds.length === 1 && title && title !== 'Statistiques joueurs'
      ? [{ name: title }]
      : [],
  [selectedCompetitionIds.length, title]
);


const widgetConfigResolved =
  !widgetConfigurationId || (!widgetConfigLoading && (widgetConfig || widgetConfigError));

const resolvedTitle = useMemo(() => {
  if (!widgetConfigResolved) return '';

  return resolveWidgetDisplayTitle({
    widgetConfig,
    competitionInfos: titleCompetitionInfos,
    fallbackTitle: title,
  });
}, [widgetConfigResolved, widgetConfig, titleCompetitionInfos, title]);

const effectiveProvider = widgetConfig?.provider || '';

const effectiveDiscipline = useMemo(() => {
  if (!allowedDisciplines.length) {
    return discipline || '';
  }

  if (discipline && allowedDisciplines.includes(discipline)) {
    return discipline;
  }

  if (
    widgetConfig?.defaultDiscipline &&
    allowedDisciplines.includes(widgetConfig.defaultDiscipline)
  ) {
    return widgetConfig.defaultDiscipline;
  }

  if (allowedDisciplines.length === 1) {
    return allowedDisciplines[0];
  }

  return '';
}, [allowedDisciplines, discipline, widgetConfig]);

const waitingForDisciplineSelection =
  allowedDisciplines.length > 1 && !effectiveDiscipline;

const disciplineConfig = useMemo(() => {
  return widgetConfig?.filtersByDiscipline?.[effectiveDiscipline] || {};
}, [widgetConfig, effectiveDiscipline]);

const seasonMode = disciplineConfig?.seasonMode === 'since' ? 'since' : 'explicit';

const configuredSeasonSince = useMemo(() => {
  return seasonMode === 'since' && disciplineConfig?.seasonSince
    ? String(disciplineConfig.seasonSince).trim()
    : '';
}, [disciplineConfig, seasonMode]);

const allowedSeasonValues = useMemo(
  () =>
    new Set(
      (disciplineConfig?.allowedSeasons || [])
        .map((v) => String(v).trim())
        .filter(Boolean)
    ),
  [disciplineConfig]
);

const defaultSeasonValues = useMemo(
  () =>
    (disciplineConfig?.defaultSeasons || [])
      .map((v) => String(v).trim())
      .filter(Boolean),
  [disciplineConfig]
);

const allowedCompetitionIds = useMemo(
  () =>
    new Set(
      (disciplineConfig?.allowedCompetitionIds || [])
        .map((v) => String(v).trim())
        .filter(Boolean)
    ),
  [disciplineConfig]
);

const defaultCompetitionIds = useMemo(
  () =>
    (disciplineConfig?.defaultCompetitionIds || [])
      .map((v) => String(v).trim())
      .filter(Boolean),
  [disciplineConfig]
);

useEffect(() => {
  seasonClearedManuallyRef.current = false;
}, [
  effectiveProvider,
  effectiveDiscipline,
  seasonMode,
  configuredSeasonSince,
  widgetConfigurationId,
]);

useEffect(() => {
  competitionClearedManuallyRef.current = false;
}, [
  effectiveProvider,
  effectiveDiscipline,
  seasonMode,
  configuredSeasonSince,
  selectedSeasons.map((option) => option.value).join(','),
  widgetConfigurationId,
]);

  useEffect(() => {
    let ignore = false;

    const loadWidgetConfig = async () => {
      if (!widgetConfigurationId) {
        if (!ignore) {
          setWidgetConfig(null);
          setWidgetConfigError('');
        }
        return;
      }

      try {
        setWidgetConfigLoading(true);
        setWidgetConfigError('');

        const { data } = await getWidgetConfiguration(widgetConfigurationId);

        if (!ignore) {
          setWidgetConfig(data || null);
        }
      } catch (e) {
        if (!ignore) {
          setWidgetConfig(null);
          setWidgetConfigError('Impossible de charger la configuration du widget.');
        }
      } finally {
        if (!ignore) {
          setWidgetConfigLoading(false);
        }
      }
    };

    loadWidgetConfig();

    return () => {
      ignore = true;
    };
  }, [widgetConfigurationId]);

useEffect(() => {
  let ignore = false;

  const loadSeasons = async () => {
    if (!effectiveProvider || !effectiveDiscipline) {
      setSeasonOptions([]);
      setSelectedSeasons([]);
      return;
    }

    if (seasonMode === 'since') {
      setSeasonOptions([]);
      setSelectedSeasons([]);

      if (configuredSeasonSince && seasonSinceParam !== configuredSeasonSince) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('season');
        nextParams.set('seasonSince', configuredSeasonSince);

        // if (effectiveProvider) nextParams.set('provider', effectiveProvider);
        if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
        if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);

        setSearchParams(nextParams, { replace: true });
      }

      return;
    }

    try {
      setLoadingSeasons(true);

      const { data } = await getCompetitionSeasonsForRankingBuilder({
        provider: effectiveProvider,
        discipline: effectiveDiscipline,
      });

      if (ignore) return;

      const rawSeasons = Array.isArray(data) ? data : [];
      const normalizedSeasons = normalizeSeasonOptions(rawSeasons).filter(
        (option) => allowedSeasonValues.size === 0 || allowedSeasonValues.has(option.value)
      );

      setSeasonOptions(normalizedSeasons);

      const preselectedFromUrl = normalizedSeasons.filter((option) =>
        selectedSeasonsFromUrl.includes(option.value)
      );

      if (preselectedFromUrl.length) {
        setSelectedSeasons(preselectedFromUrl);

        if (preselectedFromUrl.length !== selectedSeasonsFromUrl.length) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set(
            'season',
            preselectedFromUrl.map((option) => option.value).join(',')
          );
          if (widgetConfigurationId) {
            nextParams.set('widgetConfigurationId', widgetConfigurationId);
          }
          setSearchParams(nextParams, { replace: true });
        }
      } else if (normalizedSeasons.length && !seasonClearedManuallyRef.current) {
        const configuredDefaults = normalizedSeasons.filter((option) =>
          defaultSeasonValues.includes(option.value)
        );

        const nextSelected = configuredDefaults.length
          ? configuredDefaults
          : [normalizedSeasons[0]];

        setSelectedSeasons(nextSelected);

        const nextParams = new URLSearchParams(searchParams);
        nextParams.set(
          'season',
          nextSelected.map((option) => option.value).join(',')
        );
        nextParams.delete('seasonSince');

        if (widgetConfigurationId) {
          nextParams.set('widgetConfigurationId', widgetConfigurationId);
        }

        setSearchParams(nextParams, { replace: true });
      } else {
        setSelectedSeasons([]);
      }
    } catch (error) {
      if (!ignore) {
        setSeasonOptions([]);
        setSelectedSeasons([]);
      }
    } finally {
      if (!ignore) {
        setLoadingSeasons(false);
      }
    }
  };

  loadSeasons();

  return () => {
    ignore = true;
  };
}, [
  effectiveProvider,
  effectiveDiscipline,
  selectedSeasonsFromUrl,
  seasonSinceParam,
  searchParams,
  setSearchParams,
  widgetConfigurationId,
  seasonMode,
  configuredSeasonSince,
  allowedSeasonValues,
  defaultSeasonValues,
]);

useEffect(() => {
  let ignore = false;

  const loadCompetitions = async () => {
    if (!effectiveProvider || !effectiveDiscipline) {
      setCompetitions([]);
      setSelectedCompetitions([]);
      return;
    }

    try {
      setLoadingCompetitions(true);
      setCompetitionsError('');

      const effectiveSeasonSince =
        seasonMode === 'since' ? configuredSeasonSince || undefined : undefined;

      const { data } = await getCompetitionsForRankingBuilder({
        provider: effectiveProvider,
        discipline: effectiveDiscipline,
        season:
          seasonMode !== 'since' && selectedSeasons.length
            ? selectedSeasons.map((option) => option.value).join(',')
            : undefined,
        seasonSince: effectiveSeasonSince,
      });

      if (ignore) return;

      const normalized = (Array.isArray(data) ? data : []).filter(
        (competition) =>
          allowedCompetitionIds.size === 0 ||
          allowedCompetitionIds.has(String(competition._id))
      );

      setCompetitions(normalized);

      const preselected = normalized
        .filter((competition) =>
          selectedCompetitionIds.includes(String(competition._id))
        )
        .slice(0, MAX_SELECTED_COMPETITIONS);

      setSelectedCompetitions(preselected);

    const defaultCompetitions = normalized
      .filter((competition) =>
        defaultCompetitionIds.includes(String(competition._id))
      )
      .slice(0, MAX_SELECTED_COMPETITIONS);

      const fallbackCompetitions =
        normalized.length === 1 ? [normalized[0]] : [];

      const nextCompetitions =
        defaultCompetitions.length ? defaultCompetitions : fallbackCompetitions;

      if (selectedCompetitionIds.length && preselected.length !== selectedCompetitionIds.length) {
        const nextParams = new URLSearchParams(searchParams);

        if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
        if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);

        if (selectedSeasons.length) {
          nextParams.set(
            'season',
            selectedSeasons.map((option) => option.value).join(',')
          );
        } else if (seasonMode === 'since' && configuredSeasonSince) {
          nextParams.set('seasonSince', configuredSeasonSince);
        }

        if (preselected.length) {
          nextParams.set(
            'competitionIds',
            preselected.map((competition) => String(competition._id)).join(',')
          );
        } else if (nextCompetitions.length) {
          setSelectedCompetitions(nextCompetitions);

          nextParams.set(
            'competitionIds',
            nextCompetitions.map((competition) => String(competition._id)).join(',')
          );
        } else {
          nextParams.delete('competitionIds');
        }

        setSearchParams(nextParams, { replace: true });
     } else if (
        !selectedCompetitionIds.length &&
        nextCompetitions.length &&
        !competitionClearedManuallyRef.current
      ) {
        setSelectedCompetitions(nextCompetitions);

        const nextParams = new URLSearchParams(searchParams);
        if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
        if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);

        if (selectedSeasons.length) {
          nextParams.set(
            'season',
            selectedSeasons.map((option) => option.value).join(',')
          );
        } else if (seasonMode === 'since' && configuredSeasonSince) {
          nextParams.set('seasonSince', configuredSeasonSince);
        }

        nextParams.set(
          'competitionIds',
          nextCompetitions.map((competition) => String(competition._id)).join(',')
        );

        setSearchParams(nextParams, { replace: true });
      }
    } catch (error) {
      if (!ignore) {
        setCompetitions([]);
        setSelectedCompetitions([]);
        setCompetitionsError('Impossible de charger les compétitions.');
      }
    } finally {
      if (!ignore) {
        setLoadingCompetitions(false);
      }
    }
  };

  loadCompetitions();

  return () => {
    ignore = true;
  };
}, [
  effectiveProvider,
  effectiveDiscipline,
  selectedSeasons,
  selectedCompetitionIds,
  allowedCompetitionIds,
  defaultCompetitionIds,
  seasonMode,
  configuredSeasonSince,
  searchParams,
  setSearchParams,
  widgetConfigurationId,
]);

  useEffect(() => {
  let ignore = false;

  const loadMeta = async () => {
    const firstCompetitionId = selectedCompetitionIds[0];

    if (!firstCompetitionId) {
      setTitle('Statistiques joueurs');
      setMetaLoading(false);
      return;
    }

    try {
      setMetaLoading(true);
      const { data } = await getRankingCompetitionMeta(firstCompetitionId);

      if (!ignore) {
        setTitle(
          selectedCompetitionIds.length > 1
            ? 'Statistiques joueurs'
            : data?.name || 'Statistiques joueurs'
        );
      }
    } catch (error) {
      if (!ignore) {
        setTitle('Statistiques joueurs');
      }
    } finally {
      if (!ignore) {
        setMetaLoading(false);
      }
    }
  };

  loadMeta();

  return () => {
    ignore = true;
  };
}, [selectedCompetitionIds]);


//   useEffect(() => {
//   if (discipline) return;

//   const nextParams = new URLSearchParams(searchParams);
//   nextParams.set('discipline', 'rugby');
//   if (widgetConfigurationId) {
//             nextParams.set('widgetConfigurationId', widgetConfigurationId);
//           }
//   setSearchParams(nextParams, { replace: true });
// }, [discipline, searchParams, setSearchParams]);

const handleDisciplineChange = (event) => {
  const value = event.target.value;

  const nextParams = new URLSearchParams();
  // if (effectiveProvider) nextParams.set('provider', effectiveProvider);
  if (value) nextParams.set('discipline', value);
  if (widgetConfigurationId) {
            nextParams.set('widgetConfigurationId', widgetConfigurationId);
          }

  setSelectedSeasons([]);
  setSelectedCompetitions([]);

  setSearchParams(nextParams);
};

  const handleSeasonsChange = (_, value) => {
    
    seasonClearedManuallyRef.current = Array.isArray(value) && value.length === 0;
    competitionClearedManuallyRef.current = false;

    setSelectedSeasons(value);
    setSelectedCompetitions([]);

    const nextParams = new URLSearchParams();
    // if (effectiveProvider) nextParams.set('provider', effectiveProvider);
    if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
    if (widgetConfigurationId) {
            nextParams.set('widgetConfigurationId', widgetConfigurationId);
          }
    if (value.length) {
        nextParams.set(
            'season',
            value.map((option) => option.value).join(',')
        );
        }
    if (value.length === 0 && seasonMode === 'since' && configuredSeasonSince) nextParams.set('seasonSince', configuredSeasonSince);

    setSearchParams(nextParams);
  };

const handleCompetitionsChange = (_, value) => {
  const nextValue = clampCompetitionObjects(value);
  competitionClearedManuallyRef.current = nextValue.length === 0;
  setSelectedCompetitions(nextValue);

  const nextParams = new URLSearchParams();
  if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);

  if (selectedSeasons.length) {
    nextParams.set(
      'season',
      selectedSeasons.map((option) => option.value).join(',')
    );
  }

  if (!selectedSeasons.length && seasonMode === 'since' && configuredSeasonSince) {
    nextParams.set('seasonSince', configuredSeasonSince);
  }

  if (nextValue.length) {
    nextParams.set(
      'competitionIds',
      nextValue.map((competition) => String(competition._id)).join(',')
    );
  }

  if (widgetConfigurationId) {
    nextParams.set('widgetConfigurationId', widgetConfigurationId);
  }

  setSearchParams(nextParams);
};

  const canRenderWidget =
    Boolean(effectiveDiscipline) && selectedCompetitionIds.length > 0;


  const showDisciplineInput = allowedDisciplines.length > 1;

  const showSeasonInput =
    Boolean(effectiveDiscipline) &&
    seasonMode !== 'since' &&
    seasonOptions.length > 1;

  const showCompetitionInput =
    Boolean(effectiveDiscipline) &&
    competitions.length > 1;

  const shouldShowWidgetHeader = widgetConfigResolved && Boolean(resolvedTitle);

  const shouldShowWidgetFilters =
  widgetConfigResolved &&
  (
    shouldShowWidgetHeader ||
    showDisciplineInput ||
    showSeasonInput ||
    showCompetitionInput ||
    Boolean(widgetConfigError)
  );

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Stack spacing={3}>
          { shouldShowWidgetFilters && <Paper
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: '#e5e7eb',
              p: { xs: 2, md: 3 },
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {resolvedTitle}
                </Typography>
                {/* {effectiveDiscipline ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {DISCIPLINE_LABELS[effectiveDiscipline] || effectiveDiscipline}
                  </Typography>
                ) : null} */}
                {!showSeasonInput && widgetSeason ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Saison(s) : {widgetSeason}
                                  </Typography>
                                ) : null}
              </Box>

               {widgetConfigError ? (
                              <Alert severity="error">{widgetConfigError}</Alert>
                            ) : null}

              {showDisciplineInput ? (
                <>
              <TextField
                select
                fullWidth
                label="Sport"
                value={effectiveDiscipline}
                onChange={handleDisciplineChange}
              >
                <MenuItem value="">
                  <em>Sélectionner un sport</em>
                </MenuItem>
                {DISCIPLINE_OPTIONS
                  .filter((option) => allowedDisciplines.includes(option.value))
                  .map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
              </TextField>
              {waitingForDisciplineSelection ? (
                <Alert severity="info">
                  Sélectionnez un sport pour charger les saisons, compétitions et statistiques.
                </Alert>
              ) : null}
              </>

            ) : null}

            {effectiveDiscipline ? (
              <>
                  {seasonMode !== 'since' && showSeasonInput && (
                      <Autocomplete
                          multiple
                          options={seasonOptions}
                          value={selectedSeasons}
                          loading={loadingSeasons}
                          onChange={handleSeasonsChange}
                          getOptionLabel={(option) => option?.label || ''}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          renderInput={(params) => (
                          <TextField
                              {...params}
                              label="Saisons"
                              placeholder="Sélectionner une ou plusieurs saisons"
                          />
                          )}
                      />
                      )}

                    {showCompetitionInput ? (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={competitions}
                      value={selectedCompetitions}
                      loading={loadingCompetitions}
                      onChange={handleCompetitionsChange}
                      getOptionLabel={(option) => option?.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        String(option._id) === String(value._id)
                      }
                      limitTags={5}
                      getOptionDisabled={(option) =>
                        selectedCompetitions.length >= MAX_SELECTED_COMPETITIONS &&
                        !selectedCompetitions.some(
                          (selected) => String(selected._id) === String(option._id)
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Compétitions"
                          placeholder="Sélectionner jusqu’à 5 compétitions"
                          helperText={`Maximum ${MAX_SELECTED_COMPETITIONS} compétitions`}
                        />
                      )}
                    />
                      ) : null}

                    {competitionsError ? (
                      <Alert severity="error">{competitionsError}</Alert>
                    ) : null}
                     
                     </>

                     ) : null}

                  </Stack>
                </Paper>}

          {canRenderWidget ? (
            metaLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <PlayerRankingWidget
                discipline={effectiveDiscipline}
                season={widgetSeason}
                widgetConfigurationId={widgetConfigurationId}
                competitionIds={selectedCompetitionIds}
                title=""
                showCompetitionInfos={false}
                onNavigate={(url) => navigate(url)}
              />
            )
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}